import React, { useEffect, useState } from "react";
import {
  Button,
  Layout,
  Menu,
  Dropdown,
  Modal,
  Form,
  Input,
  theme,
  Grid,
  Tooltip,
} from "antd";
import axios from "axios";
import Inbox from "./inbox";
import Profile from "./dashboard";
import RunTest from "./runTest";
import {
  LogoutOutlined,
  SettingOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DownOutlined,
  InboxOutlined,
  DashboardOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import eonLogoLight from "../../assets/images/eon-logo-light.png";
import "../roleBasedTester/RoleBasedTesterDashboard.css";

const { Header, Content, Footer } = Layout;
const { Item } = Form;
const { useBreakpoint } = Grid;

const dataApiUrl =
  "https://dkrqrfvlh3.execute-api.us-east-1.amazonaws.com/dev/opts-count-per-application";

const updatePassword = process.env.REACT_APP_API_UpdatePassword;

const NAV_ITEMS = [
  {
    key: "1",
    label: "Inbox",
    icon: <InboxOutlined />,
    component: Inbox,
  },
  {
    key: "2",
    label: "Dashboard",
    icon: <DashboardOutlined />,
    component: Profile,
  },
  {
    key: "3",
    label: "Run Test",
    icon: <ThunderboltOutlined />,
    component: RunTest,
  },
];

const TesterDashboard = () => {
  const [selectedKey, setSelectedKey] = useState("1");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);
  const [form] = Form.useForm();
  const [, setFilteredApps] = useState([]);

  const user = JSON.parse(localStorage.getItem("user")) || {};
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  useEffect(() => {
    if (screens.md === false) setSidebarCollapsed(true);
  }, [screens.md]);

  const onLogout = () => {
    localStorage.removeItem("setupTime");
    localStorage.removeItem("user");
    navigate("/");
    window.location.reload();
  };

  const filterApps = (data) => {
    const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);
    const filtered = ["ghana", "mali"].map((country) => {
      const apps = Object.entries(data[country])
        .filter(([app, count]) => count > 70)
        .map(([app, count]) => ({ app, count }));
      return { country: capitalize(country), apps };
    });
    setFilteredApps(filtered);
  };

  const fetchChartData = async () => {
    try {
      const response = await fetch(dataApiUrl);
      if (!response.ok) {
        throw new Error("Failed to fetch chart data");
      }
      const data = await response.json();
      filterApps(data);
    } catch (error) {
      console.error("Error fetching chart data:", error);
    }
  };

  useEffect(() => {
    fetchChartData();
  }, []);

  const selectNavItem = (key) => {
    setSelectedKey(key);
    if (screens.md === false) setSidebarCollapsed(true);
  };

  const handlePasswordChange = () => {
    setChangePasswordVisible(true);
  };

  const handleCancel = () => {
    setChangePasswordVisible(false);
  };

  const handleFinish = async (values) => {
    try {
      const { currentPassword, newPassword } = values;
      const response = await axios.put(updatePassword, {
        userId: user.userId,
        oldPassword: currentPassword,
        newPassword: newPassword,
      });

      if (response.status === 200) {
        setChangePasswordVisible(false);
      } else {
        console.error("Password change failed");
      }
    } catch (error) {
      console.error("Error changing password:", error);
    }
  };

  const menu = (
    <Menu>
      <Menu.Item
        key="changePassword"
        icon={<SettingOutlined />}
        onClick={handlePasswordChange}
      >
        Change Password
      </Menu.Item>
      <Menu.Item key="logout" onClick={onLogout} icon={<LogoutOutlined />}>
        Logout
      </Menu.Item>
    </Menu>
  );

  const ActiveNav = NAV_ITEMS.find((item) => item.key === selectedKey);
  const ActiveComponent = ActiveNav?.component;

  return (
    <Layout
      className={`rbt-v2-layout ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}
    >
      <aside
        className={`rbt-v2-sidebar ${sidebarCollapsed ? "collapsed" : ""}`}
      >
        <div className="rbt-v2-sidebar-header">
          {!sidebarCollapsed && (
            <div className="rbt-v2-logo">
              <img
                src={eonLogoLight}
                alt="EON Testing Tool"
                className="rbt-v2-logo-img"
              />
            </div>
          )}
          <Button
            type="text"
            icon={
              sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />
            }
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="rbt-v2-collapse-btn"
          />
        </div>
        <nav className="rbt-v2-nav">
          {NAV_ITEMS.map((item) => (
            <Tooltip
              key={item.key}
              title={sidebarCollapsed ? item.label : null}
              placement="right"
            >
              <button
                type="button"
                className={`rbt-v2-nav-item ${selectedKey === item.key ? "active" : ""}`}
                onClick={() => selectNavItem(item.key)}
              >
                <span className="rbt-v2-nav-icon">{item.icon}</span>
                {!sidebarCollapsed && (
                  <span className="rbt-v2-nav-label">{item.label}</span>
                )}
              </button>
            </Tooltip>
          ))}
        </nav>
      </aside>

      <Layout className="rbt-v2-main">
        <Header className="rbt-v2-header">
          <div className="rbt-v2-header-left">
            <Button
              type="text"
              icon={
                sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />
              }
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="rbt-v2-mobile-menu-btn"
              aria-label="Toggle sidebar"
            />
            <h1 className="rbt-v2-page-title">{ActiveNav?.label || "Inbox"}</h1>
          </div>
          <div className="rbt-v2-header-right">
            <Dropdown
              overlay={menu}
              trigger={["click"]}
              placement="bottomRight"
            >
              <button type="button" className="rbt-v2-user-btn" aria-label="Account menu">
                <span className="rbt-v2-user-avatar">
                  {user.name
                    ?.split(" ")
                    .map((w) => w[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()}
                </span>
                <span className="rbt-v2-user-name">
                  {user.name
                    ?.split(" ")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")}
                </span>
                <DownOutlined className="rbt-v2-user-chevron" />
              </button>
            </Dropdown>
          </div>
        </Header>

        <Content
          className="rbt-v2-content"
          style={{
            padding: screens.xs ? "16px" : "24px",
            background: colorBgContainer,
          }}
        >
          <div className="rbt-v2-content-inner">
            {ActiveComponent && <ActiveComponent />}
          </div>
        </Content>

        <Footer className="rbt-v2-footer">
          EON Testing Tool ©{new Date().getFullYear()} Created by EON
          Intelligence
        </Footer>
      </Layout>

      <Modal
        title="Change Password"
        open={changePasswordVisible}
        onCancel={handleCancel}
        footer={null}
      >
        <Form
          form={form}
          name="changePasswordForm"
          onFinish={handleFinish}
          layout="vertical"
        >
          <Item
            name="currentPassword"
            label="Current Password"
            rules={[
              {
                required: true,
                message: "Please input your current password!",
              },
            ]}
          >
            <Input.Password />
          </Item>
          <Item
            name="newPassword"
            label="New Password"
            rules={[
              {
                required: true,
                message: "Please input your new password!",
              },
            ]}
          >
            <Input.Password />
          </Item>
          <Item
            name="confirmNewPassword"
            label="Confirm New Password"
            dependencies={["newPassword"]}
            rules={[
              {
                required: true,
                message: "Please confirm your new password!",
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("The two passwords do not match!")
                  );
                },
              }),
            ]}
          >
            <Input.Password />
          </Item>
          <Item>
            <Button
              type="secondary"
              className="bg-[blue] text-[white]"
              htmlType="submit"
            >
              Change Password
            </Button>
          </Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default TesterDashboard;
